// src/pages/MockTest/attempt/[attemptId]/components/RunnerErrorBoundary.tsx
//
// A crash here costs a student a live, timed attempt, so the runner gets its
// own boundary rather than relying on the app-level one.
//
// The copy is deliberately precise about what survives. Answers are POSTed
// on "Save & next" — NOT on selection — so a saved answer is on the server
// and an unsaved selection is not. Telling a panicking student "everything
// is safe" would be a lie, and they'd find out the expensive way.
//
// Reloading is the primary action because the attempt is resumed from the
// server on mount, so it genuinely restores saved progress.

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  attemptId?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RunnerErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the stack in the console for triage — this is the one screen
    // where a silent failure is unacceptable.
    console.error("Mock runner crashed:", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="flex w-full max-w-md flex-col gap-4 rounded-xl border border-border bg-card p-6">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-lg font-semibold tracking-[-0.01em]">
              Something went wrong on this screen
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              <strong className="font-semibold text-foreground">
                Every answer you saved is on the server.
              </strong>{" "}
              Reloading restores them and your remaining time. If you had an
              option selected but hadn&apos;t pressed{" "}
              <span className="font-medium text-foreground">Save &amp; next</span>{" "}
              yet, you&apos;ll need to pick that one again.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Reload the test
            </button>
            <a
              href="/mock-tests/my-tests"
              className="rounded-lg border border-input px-4 py-2.5 text-sm font-medium transition-colors hover:bg-row-hover"
            >
              Go to My Tests
            </a>
          </div>

          {this.state.error?.message && (
            <p className="break-words font-mono text-[11px] text-muted-foreground">
              {this.state.error.message}
            </p>
          )}
        </div>
      </div>
    );
  }
}
